import React from 'react';
import PropTypes from 'prop-types';
import startCase from 'lodash/startCase';
import isEmpty from 'lodash/isEmpty';
import isString from 'lodash/isString';

import Align from '@marvelapp/marvel-ui/align';
import Button from '@marvelapp/marvel-ui/button';
import DividerText from '@marvelapp/marvel-ui/divider-text';
import Grid, { GridItem } from '@marvelapp/marvel-ui/grid';
import Spacing from '@marvelapp/marvel-ui/spacing';

import { RESET_PASSWORD } from 'marketing-website/constants/urls';
import { getUrlWithRedirect } from 'marketing-website/utils/forms';
import { isIOSOrIPadOS } from 'marketing-website/utils/deviceDetection';
import FormHeader from 'marketing-website/components/modules/FormHeader';

import FormInput from './FormInput';
import FormSelect from './FormSelect';
import FormSuccess from './../FormSuccess';
import GoogleButton from './GoogleButton';

import styles from './style/form-wrap.module.css';

export default function FormWrap({
  action,
  autocomplete,
  backendErrorMsg,
  buttonProps,
  children,
  fields,
  formSuccess,
  googleButtonText,
  headerAlign,
  invalidFields,
  isPending,
  isSignIn,
  isSSOSignIn,
  messaging,
  redirectTo,
  setFieldValue,
  showSecondaryLinks = !isIOSOrIPadOS() && !isSSOSignIn && !!process.env.CLIENT,
  validateForm,
  validFields,
}) {
  const hasBackendError =
    (isString(backendErrorMsg) && backendErrorMsg.trim() !== '') ||
    !isEmpty(backendErrorMsg);
  const disableAutoComplete = autocomplete === 'off';
  let disableFormFields = null;
  if (disableAutoComplete) {
    disableFormFields = (
      <div>
        <input className="display-none" type="text" name="name" />
        <input className="display-none" type="password" name="password" />
      </div>
    );
  }

  const resetPasswordLinkWithRedirect = getUrlWithRedirect(RESET_PASSWORD);

  return (
    <form
      onSubmit={validateForm}
      className={styles.form}
      noValidate
      autoComplete={autocomplete}
      autoCorrect="off"
      spellCheck="off"
      method="post"
      action={action} // * We need action, in case JS doesn't load properly and fails on production
      style={{ maxWidth: 400 }}
    >
      {messaging && <FormHeader align={headerAlign} messaging={messaging} />}
      {disableFormFields}
      {formSuccess && <FormSuccess message={formSuccess} />}
      {hasBackendError && children}
      {fields.map((field, index) => {
        const shouldShowTick =
          !(
            backendErrorMsg.fields &&
            backendErrorMsg.fields.indexOf(field.name) !== -1
          ) &&
          !(backendErrorMsg && typeof backendErrorMsg === 'string') &&
          validFields.indexOf(field.name) !== -1;
        const shouldShowValidationError =
          invalidFields.indexOf(field.name) !== -1;
        const isInputInvalid =
          shouldShowValidationError ||
          (backendErrorMsg.fields &&
            backendErrorMsg.fields.indexOf(field.name) !== -1) ||
          (backendErrorMsg && typeof backendErrorMsg === 'string');
        const hasForgotPasswordLink = isSignIn && field.label === 'Password';

        if (field.class === 'select') {
          return (
            <Spacing type="margin" size="m" position="top" key={field.name}>
              <FormSelect
                errorMessage={field.errorMsg}
                focusOnRender={index === 0}
                options={field.options}
                isInputInvalid={isInputInvalid}
                label={field.label}
                name={field.name}
                tabIndex={index + 1}
                onChange={e => setFieldValue(e, field.name)}
                shouldShowValidationError={shouldShowValidationError}
                type={field.type}
              />
            </Spacing>
          );
        }
        return (
          <Spacing type="margin" size="m" position="top" key={field.name}>
            <FormInput
              errorMessage={field.errorMsg}
              focusOnRender={index === 0}
              helpLink={hasForgotPasswordLink && resetPasswordLinkWithRedirect}
              isInputInvalid={isInputInvalid}
              label={field.label}
              name={field.name}
              tabIndex={index + 1}
              onChange={e => setFieldValue(e, field.name)}
              placeholder={field.placeholder || startCase(field.name)}
              shouldShowTick={shouldShowTick}
              isSSOSignIn={isSSOSignIn}
              shouldShowValidationError={shouldShowValidationError}
              type={field.type}
            />
          </Spacing>
        );
      })}
      <Spacing type="padding" size="m" position="top">
        <Button
          background={buttonProps.color || 'blue'}
          fullWidth
          hover={buttonProps.color || 'blue'}
          isLoading={isPending}
          minWidth={buttonProps.minWidth}
          size="large"
          type="submit"
          isStateful
          tabIndex={9}
          hasSucceeded={!!formSuccess}
        >
          {buttonProps.text}
        </Button>
      </Spacing>
      {showSecondaryLinks && (
        <Spacing type="margin" sizeM="m" position="top">
          <Spacing type="padding" size="m" position="bottom">
            <DividerText>Or</DividerText>
          </Spacing>
          <Align type="center">
            <Grid columns={1}>
              <GridItem columnSpan={1}>
                <GoogleButton block redirectTo={redirectTo}>
                  {googleButtonText || 'Sign up with Google'}
                </GoogleButton>
              </GridItem>
            </Grid>
          </Align>
        </Spacing>
      )}
    </form>
  );
}

FormWrap.propTypes = {
  action: PropTypes.string.isRequired,
  autocomplete: PropTypes.string,
  backendErrorMsg: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  buttonProps: PropTypes.object.required,
  children: PropTypes.object.isRequired,
  fields: PropTypes.array.isRequired,
  formSuccess: PropTypes.string,
  googleButtonText: PropTypes.string,
  headerAlign: PropTypes.string,
  invalidFields: PropTypes.array,
  isPending: PropTypes.bool.isRequired,
  isSignIn: PropTypes.bool,
  isSSOSignIn: PropTypes.bool,
  showSecondaryLinks: PropTypes.bool,
  messaging: PropTypes.string.isRequired,
  redirectTo: PropTypes.func,
  setFieldValue: PropTypes.func.isRequired,
  validateForm: PropTypes.func.isRequired,
  validFields: PropTypes.array,
};

FormWrap.defaultProps = {
  autocomplete: 'off',
  backendErrorMsg: {},
  buttonFullWidth: false,
  headerAlign: 'left',
  isSignIn: false,
  isSSOSignIn: false,
};



// WEBPACK FOOTER //
// ./src/components/modules/FormWrap/index.js